import { useEffect, useState, useCallback } from 'react'
import type { TelegramWebApp, TelegramUser } from '@/types/telegram'

interface UseTelegramWebAppReturn {
  webApp: TelegramWebApp | null
  user: TelegramUser | null
  isReady: boolean
  colorScheme: 'light' | 'dark'
  themeParams: TelegramWebApp['themeParams']
  showAlert: (message: string) => Promise<void>
  showConfirm: (message: string) => Promise<boolean>
  hapticFeedback: {
    impact: (style?: 'light' | 'medium' | 'heavy') => void
    notification: (type: 'error' | 'success' | 'warning') => void
    selection: () => void
  }
  mainButton: {
    show: (text: string, onClick: () => void) => void
    hide: () => void
    setLoading: (loading: boolean) => void
  }
  backButton: {
    show: (onClick: () => void) => void
    hide: () => void
  }
  expand: () => void
  close: () => void
  ready: () => void
}

export function useTelegramWebApp(): UseTelegramWebAppReturn {
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null)
  const [isReady, setIsReady] = useState(false)
  const [user, setUser] = useState<TelegramUser | null>(null)

  useEffect(() => {
    // Check if we're running in Telegram
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp
      setWebApp(tg)
      setUser(tg.initDataUnsafe.user || null)

      // Apply theme params to CSS variables
      if (tg.themeParams) {
        const root = document.documentElement
        Object.entries(tg.themeParams).forEach(([key, value]) => {
          if (value) {
            root.style.setProperty(`--tg-theme-${key.replace(/_/g, '-')}`, value)
          }
        })
      }

      // Set ready state
      setIsReady(true)

      // Initialize the app
      tg.ready()

      // Expand to full height
      tg.expand()

      // Apply viewport height
      const setViewportHeight = () => {
        document.documentElement.style.setProperty(
          '--tg-viewport-height',
          `${tg.viewportHeight}px`
        )
        document.documentElement.style.setProperty(
          '--tg-viewport-stable-height',
          `${tg.viewportStableHeight}px`
        )
      }
      setViewportHeight()

      // Listen for viewport changes
      tg.onEvent('viewportChanged', setViewportHeight)

      return () => {
        tg.offEvent('viewportChanged', setViewportHeight)
      }
    } else {
      // Fallback for development outside Telegram
      console.warn('Telegram WebApp not available, using mock data')
      setIsReady(true)
      setUser({
        id: 123456789,
        first_name: 'Test',
        last_name: 'User',
        username: 'testuser',
        language_code: 'en'
      })
    }
  }, [])

  const showAlert = useCallback((message: string): Promise<void> => {
    return new Promise((resolve) => {
      if (webApp) {
        webApp.showAlert(message, resolve)
      } else {
        alert(message)
        resolve()
      }
    })
  }, [webApp])

  const showConfirm = useCallback((message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      if (webApp) {
        webApp.showConfirm(message, resolve)
      } else {
        resolve(confirm(message))
      }
    })
  }, [webApp])

  const hapticFeedback = {
    impact: useCallback((style: 'light' | 'medium' | 'heavy' = 'medium') => {
      webApp?.HapticFeedback.impactOccurred(style)
    }, [webApp]),

    notification: useCallback((type: 'error' | 'success' | 'warning') => {
      webApp?.HapticFeedback.notificationOccurred(type)
    }, [webApp]),

    selection: useCallback(() => {
      webApp?.HapticFeedback.selectionChanged()
    }, [webApp])
  }

  const mainButton = {
    show: useCallback((text: string, onClick: () => void) => {
      if (webApp) {
        webApp.MainButton.setText(text)
        webApp.MainButton.onClick(onClick)
        webApp.MainButton.show()
      }
    }, [webApp]),

    hide: useCallback(() => {
      webApp?.MainButton.hide()
    }, [webApp]),

    setLoading: useCallback((loading: boolean) => {
      if (webApp) {
        if (loading) {
          webApp.MainButton.showProgress()
        } else {
          webApp.MainButton.hideProgress()
        }
      }
    }, [webApp])
  }

  const backButton = {
    show: useCallback((onClick: () => void) => {
      if (webApp) {
        webApp.BackButton.onClick(onClick)
        webApp.BackButton.show()
      }
    }, [webApp]),

    hide: useCallback(() => {
      webApp?.BackButton.hide()
    }, [webApp])
  }

  const expand = useCallback(() => {
    webApp?.expand()
  }, [webApp])

  const close = useCallback(() => {
    webApp?.close()
  }, [webApp])

  const ready = useCallback(() => {
    webApp?.ready()
  }, [webApp])

  return {
    webApp,
    user,
    isReady,
    colorScheme: webApp?.colorScheme || 'light',
    themeParams: webApp?.themeParams || {},
    showAlert,
    showConfirm,
    hapticFeedback,
    mainButton,
    backButton,
    expand,
    close,
    ready
  }
}